<div class="edit-owner">
    <fieldset class="edit-owner-form">
        <div class="avatar">
            {#if uploadInProgress}
                <div class="avatar-uploading">
                    <LoadingState visible size="large" iconClass="white-spinner" />
                    <div>{terms.pictureIsUploading}</div>
                </div>
            {:else}
                <Avatar src={unsavedOwner.pictureUrl} size=600 />
            {/if}
            <div class="avatar-actions">
                <input
                    bind:this={fileInput}
                    type="file"
                    on:change={onUserPictureFilePicked}
                    accept="image/*"
                    multiple="false"
                    hidden />
                <button bind:this={uploadAvatarButton} type="button" class="upload-avatar" on:click={onChangeUserPicture}>{terms.changePicture}</button>
                {#if unsavedOwner.hasUploadedPhoto}
                    <button type="button" class="delete-avatar" on:click={onDeleteUserPicture}>{terms.removePicture}</button>
                {/if}
            </div>
        </div>
        {#if uploadError.length > 0}
            <div class="avatar-upload-error">
                {uploadError}
            </div>
        {/if}
        <TextField
            id="name"
            required
            label={terms.name}
            placeholder={terms.nameWatermark}
            type="text"
            bind:value={unsavedOwner.name}
            disabled={saveInProgress}
            minlength="1"
            maxlength={nameMaxLength}
            errorMessage={terms.nameValidationMessage}
            hasError={nameHasErrors} />
        {#if canEditEmail}
            <TextField
                id="email"
                label={terms.email}
                placeholder={terms.emailWatermark}
                type="email"
                bind:value={unsavedOwner.email}
                disabled={saveInProgress}
                maxlength="128"
                errorMessage={terms.emailValidationMessage}
                hasError={emailHasErrors}/>
        {/if}
        <div class="edit-owner-textarea">
            <TextField
                id="aboutMe"
                label={terms.aboutMe}
                placeholder={terms.aboutMeWatermark}
                type="area"
                bind:value={unsavedOwner.summary}
                disabled={saveInProgress}
                maxlength={summaryMaxLength}
                height="medium" />
        </div>
    </fieldset>
    <footer class="edit-owner-buttons">
        <Button type="basic" text={terms.save} htmlType="button" on:click={onSaveClick} disabled={saveInProgress} on:blur={onSaveBlur}/>
        <div aria-live="assertive" role="alert" class="screen-reader">
            {#if screenReaderErrorMessages && screenReaderErrorMessages.length > 0}
                {screenReaderErrorMessages}
            {/if}
        </div>
        <Button type="secondary" text={terms.cancel} htmlType="button" on:click={onCancelClick} disabled={saveInProgress} />
    </footer>
</div>
<script>
    import { onMount, createEventDispatcher } from 'svelte';

    import Avatar from '@itslearning/prometheus/assets/images/Avatar/v1/Avatar.svelte';
    import Button from '@itslearning/prometheus/assets/inputs/Button/v1/Button.svelte';
    import TextField from '@itslearning/prometheus/assets/inputs/TextField/v1/TextField.svelte';
    import LoadingState from '@itslearning/prometheus/assets/nodes/LoadingSpinner/v1/LoadingSpinner.svelte';

    import { put } from '@itslearning/atlas/network/fetch';
    import { uploadFileToFileRepo } from '@itslearning/atlas/network/fileRepo';
    import { validateFile, fileErrors } from '@itslearning/atlas/files/validation';
    import { copyOwner, createAntiForgeryHeader } from '../common';
    import { userPictureAction as userPictureActionConstants } from '../constants';

    const dispatch = createEventDispatcher();

    export let owner = {};
    export let unsavedOwner = {};
    export let terms = {};
    export let urls = {};
    export let canEditEmail = {};
    export let antiForgeryHeaderSettings = {};
    export let fileRepoSettings = {};
    export let fileValidationSettings = {};
    export let userPictureAction = userPictureActionConstants.none;
    export let userPictureUploadedFileMetadata = null;
    export let saveInProgress = false;
    export let uploadInProgress = false;
    export let uploadError = '';
    export let screenReaderErrorMessages = undefined;
    export let nameMaxLength = 0;
    export let summaryMaxLength = 0;

    $: nameHasErrors = !unsavedOwner.name || unsavedOwner.name.trim().length === 0;
    $: emailHasErrors = hasEmailErrors(unsavedOwner);

    let fileInput;
    let uploadAvatarButton;

    function hasEmailErrors(unsavedOwner) {
        if (!unsavedOwner.email || unsavedOwner.email.length === 0) {
            return false;
        }

        // eslint-disable-next-line
        const emailValidationRegexp = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

        return !emailValidationRegexp.test(String(unsavedOwner.email).toLowerCase());
    }

    onMount(() => {
        unsavedOwner = copyOwner(owner);
    });

    export function setFocusOnUploadAvatarButton() {
        uploadAvatarButton.focus();
    }

    function onSaveBlur() {
        screenReaderErrorMessages = undefined;
    }

    function onSaveClick() {
        if (emailHasErrors || nameHasErrors) {
            let errorMessages = '';

            if (nameHasErrors) {
                errorMessages += terms.nameValidationMessage;
            }

            if (emailHasErrors) {
                errorMessages += terms.emailValidationMessage;
            }

            screenReaderErrorMessages = errorMessages;

            return;
        }

        if (saveInProgress) {
            return;
        }

        saveInProgress = true;

        put(urls.updateOwnerInformation, {
            Name: unsavedOwner.name,
            Email: unsavedOwner.email,
            Summary: unsavedOwner.summary,
            UserPictureFileMetadata: userPictureUploadedFileMetadata,
            UserPictureAction: userPictureAction
        }, {
            headers: createAntiForgeryHeader(antiForgeryHeaderSettings)
        })
            .then(data => {
                screenReaderErrorMessages = terms.saved;
                const owner = unsavedOwner;

                owner.name = data.name;
                owner.email = data.email;
                owner.summary = data.summary;
                owner.pictureUrl = data.pictureUrl;
                owner.hasUploadedPhoto = data.hasUploadedPhoto;
                saveInProgress = false;
                dispatch('savePortfolioOwner', { owner: unsavedOwner });
            })
            .catch(() => {
                saveInProgress = false;
            });
    }

    function onCancelClick() {
        if (saveInProgress) {
            return;
        }

        dispatch('cancelEditPortfolioOwner', {});
    }

    function onChangeUserPicture() {
        if (uploadInProgress) {
            return;
        }

        uploadError = '';
        fileInput.click();
    }

    function onUserPictureFilePicked() {
        const files = [];

        for (let i = 0; i < fileInput.files.length; i++) {
            files.push(fileInput.files[i]);
        }

        const file = files.pop();

        if (!file) {
            return;
        }

        try {
            uploadInProgress = true;

            const validation = validateFile(file, fileValidationSettings);

            if (!validation.valid) {
                handleUploadUserPictureError(validation.error);

                return;
            }

            uploadFileToFileRepo(file, fileRepoSettings)
                .then(fileRepoFile => {
                    if (fileRepoFile.error) {
                        handleUploadUserPictureError(fileRepoFile.error);

                        return;
                    }

                    const owner = unsavedOwner;

                    owner.pictureUrl = URL.createObjectURL(file);
                    owner.hasUploadedPhoto = true;

                    unsavedOwner = owner;
                    userPictureUploadedFileMetadata = fileRepoFile;
                    userPictureAction = userPictureActionConstants.uploadNew;
                    uploadInProgress = false;
                });
        } catch {
            handleUploadUserPictureError(fileErrors.unknown);
        }
    }

    function onDeleteUserPicture() {
        if (uploadInProgress) {
            return;
        }

        uploadError = '';
        userPictureUploadedFileMetadata = null;
        userPictureAction = userPictureActionConstants.delete;

        const owner = unsavedOwner;

        owner.pictureUrl = urls.genericUserPicture;
        owner.hasUploadedPhoto = false;
        unsavedOwner = owner;
    }

    function handleUploadUserPictureError(fileError) {
        let errorText;

        switch (fileError) {
            case fileErrors.tooLarge:
                errorText = terms.uploadUserPictureTerms.fileTooLarge;
                break;
            case fileErrors.tooSmall:
                errorText = terms.uploadUserPictureTerms.fileTooLSmall;
                break;
            case fileErrors.invalidExtension:
                errorText = terms.uploadUserPictureTerms.invalidFileType;
                break;
            case fileErrors.invalidFileName:
                errorText = terms.uploadUserPictureTerms.invalidFileNameCharacters;
                break;
            case fileErrors.nameTooLong:
                errorText = terms.uploadUserPictureTerms.fileNameTooLong;
                break;
            default:
                errorText = terms.uploadUserPictureTerms.uploadError;
                break;
        }

        uploadError = errorText;
        uploadInProgress = false;
    }
</script>
